import Autolinker from 'autolinker'
import striptags from 'striptags'
import { Channel } from '@/constants/user/channels'
import { Roles } from '@/constants/user/roles'

export function stripDescriptionTags(description?: string | null, { stripLinks = false } = {}) {
  description = trimDescription(description)
  description = striptags(description, ['h1', 'h2', 'h3', 'li', 'p', 'br'])
  description = striptags(description, ['br'], '<br>')
  description = description.replace(/(<br \/>|<br>){2,}/g, '<br>')
  description = description.replace(/\s{2,}/g, '')
  const firstBr = description.startsWith('<br>') ? 4 : 0
  const lastBr = description.endsWith('<br>') ? description.length - 4 : description.length
  description = description.substr(firstBr, lastBr - firstBr)
  return stripLinks ? description : replaceLinks({ text: description })
}

export function formatDescription(description = '', tag?: string) {
  let text = tag ? wrapInTag(description, tag) : description
  text = trimDescription(text)
  text = replaceALinks({ text, secure: true })
  return replaceLinks({ text, secure: true })
}

export function replaceALinks({ text, secure }: { text: string; secure?: boolean }) {
  const reg = /(<a[^>]*href=")([^"]*)("[^>]*>)/gi
  const replacer = function (match: string, p1: string, p2: string, p3: string) {
    if (secure && !p2?.startsWith(process.env.VUE_APP_FRONTEND_URL || '')) {
      const secureUrl = `/leave?url=${p2}`
      return `${p1}${secureUrl}${p3}`
    }
  }
  // @ts-ignore
  return text.replace(reg, replacer)
}

export function replaceLinks({ text, secure }: { text: string; secure?: boolean }) {
  return Autolinker.link(text, {
    newWindow: !secure,
    className: 'auto-link',
    stripPrefix: false,
    replaceFn: function (match) {
      let tag = match.buildTag()
      const matchedUrl = match.getAnchorHref() || ''
      if (secure && !matchedUrl?.startsWith(process.env.VUE_APP_FRONTEND_URL || '')) {
        const secureUrl = `/leave?url=${matchedUrl}`
        tag.setAttr('href', secureUrl)
      }
      tag.addClass('lx-new-link')
      tag.setAttr('rel', 'nofollow noopener')
      return tag
    },
  })
}

export function stripWithLinks(text: string, secure?: boolean) {
  const strips = striptags(text)
  return replaceLinks({ text: strips, secure })
}

export function getSocialLink(channel: Channel, nick?: string) {
  if (!nick) return nick
  if (channel === Channel.Facebook) {
    return getFacebookLink(nick)
  }
  let domain = getSocialDomain(channel)
  if (nick.startsWith('@')) {
    return `${domain}${nick.substr(1)}`
  }
  if (nick.startsWith('https://')) {
    return nick
  }
  return `${domain}${nick}`
}

function getSocialDomain(channel: Channel) {
  switch (channel) {
    case Channel.LinkedIn:
      return 'https://www.linkedin.com/company/'
    case Channel.Twitter:
      return 'https://x.com/'
    case Channel.Github:
      return 'https://github.com/'
    default:
      return '//'
  }
}

function getFacebookLink(raw: string): string {
  const value = raw.trim()

  // Already full URL with protocol.
  if (/^https?:\/\//i.test(value)) {
    return value
  }

  // Begins with facebook.com or www.facebook.com without protocol.
  if (/^(?:www\.)?facebook\.com/i.test(value)) {
    return `https://${value.replace(/^\/+/, '')}`
  }

  // id=123... -> profile.php?id=123...
  if (/^id=\d+/i.test(value)) {
    return `https://www.facebook.com/profile.php?${value}`
  }

  // Starts with profile.php?
  if (/^profile\.php\?/i.test(value)) {
    return `https://www.facebook.com/${value}`
  }

  // Pure numeric id -> profile.php?id=123
  if (/^\d+$/i.test(value)) {
    return `https://www.facebook.com/profile.php?id=${value}`
  }

  // Username starting with @
  if (value.startsWith('@')) {
    return `https://www.facebook.com/${value.substr(1)}`
  }

  // Fallback
  return `https://www.facebook.com/${value}`
}

/**
 * Returns url for OAuth login or registrations
 * @param {String} social authclient query param
 */
export function getOAuthLink(social: string, profileType: Roles = Roles.FREELANCER, viaLogin: boolean = true) {
  if (process.client) {
    const domain = `${window.location.protocol}//${window.location.host}`
    const role = profileType ? `&profileType=${profileType}` : ''
    const module = viaLogin ? 1 : 0
    // eslint-disable-next-line max-len
    return `${process.env.VUE_APP_BACKEND_CLIENT_URL}oauth/connect?authclient=${social}&module=${module}&successRegisterUrl=${domain}&successAuthUrl=${domain}/auth/social&failureUrl=${domain}&faUrl=${domain}/&tokenMode=url${role}`
  }
}

export function humanizeBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Detect search crawler bots
 * @param userAgent
 * @returns {Boolean}
 */
export function isCrawler(userAgent?: Navigator['userAgent']) {
  const bots =
    // eslint-disable-next-line max-len
    /google|bot|crawl|spider|slurp|baidu|bing|msn|teoma|yandex|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|biglotron|convera|gigablast|archive|webmon|httrack|grub|netresearchserver|speedy|fluffy|bibnum|findlink|panscient|IOI|ips-agent|yanga|Voyager|CyberPatrol|postrank|page2rss|linkdex|ezooms|heritrix|findthatfile|Aboundex|summify|ec2linkfinder|facebook|slack|instagram|pinterest|reddit|twitter|whatsapp|yeti|RetrevoPageAnalyzer|sogou|wotbox|ichiro|drupact|coccoc|integromedb|siteexplorer|proximic|changedetection|WeSEE|scrape|scaper|g00g1e|binlar|indexer|MegaIndex|ltx71|BUbiNG|Qwantify|lipperhey|y!j-asr|AddThis/i
  return bots.test(userAgent || '')
}

export function trimDescription(description?: string | null) {
  return (
    (description || '')
      // eslint-disable-next-line no-irregular-whitespace
      .replace(/<p>[(<br />|<br>)\\s&nbsp; ]{1,}<\/p>/g, '')
  )
}

export function trimCardsDescription(description?: string | null) {
  return (description || '').replace(/(?:\r\n|\r|\n|<br>|<br\/>|<br \/>)/g, ' ')
}

export function trimWords(description?: string | null, countWords: number = 35) {
  let counter = 0
  let text = description || ''
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ') {
      counter++
      if (counter === countWords) {
        const newText = text.slice(0, i)
        return i < text.length - 1 ? `${newText}...` : newText
      }
    }
  }
  return text
}

export function formatCardsDescription(description = '') {
  let text = trimCardsDescription(description)
  return trimWords(text)
}

export function wrapInTag(text: string, tag = 'p') {
  if (text.trim().startsWith(`<${tag}>`)) return text
  return `<${tag}>${text}</${tag}>`
}
